/**
 * React hook for managing direct messages
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  type DAGStore,
  type DAGObject,
  type Identity,
  type MessageContent,
  isMessage,
  createMessageContent,
  decryptMessage,
  getOtherParty,
  isConversationMessage,
} from '@amorphous/core';

/**
 * A conversation with another user
 */
export interface Conversation {
  /** The other user's public key */
  peerPubkey: string;
  /** Most recent message in the conversation */
  lastMessage?: {
    hash: string;
    text: string;
    timestamp: number;
    fromMe: boolean;
  };
  /** Number of unread messages */
  unreadCount: number;
  /** All message hashes in the conversation (for lazy loading) */
  messageHashes: string[];
}

/**
 * A decrypted message with metadata
 */
export interface ChatMessage {
  hash: string;
  sender: string;
  recipient: string;
  text: string;
  attachments?: string[];
  timestamp: number;
  composedAt?: number;
  replyTo?: string;
  fromMe: boolean;
}

interface UseMessagesOptions {
  /** DAG store */
  store: DAGStore | null;
  /** Current user's identity */
  identity: Identity | null;
  /** Broadcast new objects to peers */
  broadcastObject?: (obj: DAGObject) => void;
}

interface UseMessagesResult {
  /** All conversations */
  conversations: Conversation[];
  /** Get messages for a specific conversation */
  getConversation: (peerPubkey: string) => Promise<ChatMessage[]>;
  /** Send a message to a user */
  sendMessage: (recipientPubkey: string, text: string, options?: {
    attachments?: string[];
    replyTo?: string;
  }) => Promise<string>;
  /** Mark conversation as read */
  markConversationRead: (peerPubkey: string) => void;
  /** Loading state */
  loading: boolean;
  /** Refresh conversations */
  refresh: () => Promise<void>;
}

export function useMessages({
  store,
  identity,
  broadcastObject,
}: UseMessagesOptions): UseMessagesResult {
  const [allMessages, setAllMessages] = useState<DAGObject<MessageContent>[]>([]);
  const [readConversations, setReadConversations] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const myPubkey = identity?.publicKey ?? '';

  // Load all messages from store
  const refresh = useCallback(async () => {
    if (!store || !myPubkey) {
      setLoading(false);
      return;
    }

    try {
      const all = await store.getAll();
      const messages = all.filter(obj => {
        if (!isMessage(obj)) return false;
        // Only include messages where I'm sender or recipient
        return obj.creator === myPubkey || obj.content.recipient === myPubkey;
      }) as DAGObject<MessageContent>[];

      // Sort by timestamp
      messages.sort((a, b) => a.timestamp - b.timestamp);
      setAllMessages(messages);
    } catch (err) {
      console.error('[Messages] Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }, [store, myPubkey]);

  // Initial load
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Refresh periodically to catch new messages
  useEffect(() => {
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [refresh]);

  // Build conversations from messages
  const conversations = useMemo((): Conversation[] => {
    if (!myPubkey) return [];

    const convMap = new Map<string, Conversation>();

    for (const msg of allMessages) {
      const peerPubkey = getOtherParty(msg, myPubkey);

      let conv = convMap.get(peerPubkey);
      if (!conv) {
        conv = {
          peerPubkey,
          messageHashes: [],
          unreadCount: 0,
        };
        convMap.set(peerPubkey, conv);
      }

      conv.messageHashes.push(msg.hash);

      // Count unread (messages from peer after last read)
      if (msg.creator !== myPubkey && !readConversations.has(peerPubkey)) {
        conv.unreadCount++;
      }

      // Update last message (messages are sorted, so last one wins)
      conv.lastMessage = {
        hash: msg.hash,
        text: '[encrypted]', // Will be decrypted when viewing
        timestamp: msg.timestamp,
        fromMe: msg.creator === myPubkey,
      };
    }

    // Sort by most recent
    return Array.from(convMap.values()).sort((a, b) => {
      const aTime = a.lastMessage?.timestamp ?? 0;
      const bTime = b.lastMessage?.timestamp ?? 0;
      return bTime - aTime;
    });
  }, [allMessages, myPubkey, readConversations]);

  // Get decrypted messages for a conversation
  const getConversation = useCallback(async (peerPubkey: string): Promise<ChatMessage[]> => {
    if (!myPubkey) return [];

    const conversationMessages = allMessages.filter(msg =>
      isConversationMessage(msg, myPubkey, peerPubkey)
    );

    const decrypted: ChatMessage[] = [];

    for (const msg of conversationMessages) {
      try {
        const otherPubkey = getOtherParty(msg, myPubkey);
        const payload = await decryptMessage(
          myPubkey,
          otherPubkey,
          msg.content.encrypted,
          msg.content.iv
        );

        decrypted.push({
          hash: msg.hash,
          sender: msg.creator,
          recipient: msg.content.recipient,
          text: payload.text,
          attachments: payload.attachments,
          timestamp: msg.timestamp,
          composedAt: payload.composedAt,
          replyTo: msg.content.replyTo,
          fromMe: msg.creator === myPubkey,
        });
      } catch (err) {
        console.error('[Messages] Failed to decrypt:', msg.hash, err);
        // Include a placeholder for failed decryption
        decrypted.push({
          hash: msg.hash,
          sender: msg.creator,
          recipient: msg.content.recipient,
          text: '[Unable to decrypt]',
          timestamp: msg.timestamp,
          fromMe: msg.creator === myPubkey,
        });
      }
    }

    return decrypted;
  }, [allMessages, myPubkey]);

  // Send a message
  const sendMessage = useCallback(async (
    recipientPubkey: string,
    text: string,
    options?: {
      attachments?: string[];
      replyTo?: string;
    }
  ): Promise<string> => {
    if (!identity || !store) {
      throw new Error('Not initialized');
    }

    // Create encrypted message content
    const { type, content } = await createMessageContent(
      identity.publicKey,
      recipientPubkey,
      text,
      options
    );

    // Create and sign the DAG object
    const msgObj = identity.createObject(type, content);

    // Store locally
    const hash = await store.put(msgObj);

    // Broadcast to peers
    if (broadcastObject) {
      broadcastObject(msgObj);
    }

    // Refresh to update conversations
    await refresh();

    return hash;
  }, [identity, store, broadcastObject, refresh]);

  // Mark conversation as read
  const markConversationRead = useCallback((peerPubkey: string) => {
    setReadConversations(prev => {
      const next = new Set(prev);
      next.add(peerPubkey);
      return next;
    });
  }, []);

  return {
    conversations,
    getConversation,
    sendMessage,
    markConversationRead,
    loading,
    refresh,
  };
}
